/**
 * @fileoverview Configuration d'un rôle backoffice.
 *
 * Sous-page dédiée à un seul rôle. Elle évite de charger la page d'aperçu des
 * rôles avec toute la configuration, tout en gardant les permissions groupées
 * par famille et les agents concernés dans un onglet séparé.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  DataCard,
  DataTable,
  EmptyState,
  MetricCard,
  PageHeader,
  SectionHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@gnpc/ui';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  ALL_CAPABILITIES,
  BACKOFFICE_ROLES,
  CAPABILITY_GROUPS,
  CAPABILITY_LABELS,
  ROLE_CAPABILITIES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  USERS,
  formatDateTime,
  type BackofficeRole,
  type BackofficeUser,
  type Capability,
} from '../data/backoffice.ts';
import { roleTone } from '../features/roles/rolePermissions.tsx';

const USER_STATUS_LABELS: Record<BackofficeUser['status'], string> = {
  ACTIVE: 'Actif',
  INVITED: 'Invité',
  DISABLED: 'Désactivé',
};

const userColumns: ColumnDef<BackofficeUser>[] = [
  {
    accessorKey: 'fullName',
    header: 'Agent',
    cell: ({ row }) => (
      <div>
        <p className="text-quipux-navy-950 font-medium">{row.original.fullName}</p>
        <p className="text-muted-foreground text-xs">{row.original.email}</p>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Statut',
    cell: ({ row }) => (
      <Badge variant={row.original.status === 'ACTIVE' ? 'default' : 'outline'}>
        {USER_STATUS_LABELS[row.original.status]}
      </Badge>
    ),
  },
  { accessorKey: 'activeSessions', header: 'Sessions' },
  {
    accessorKey: 'lastLoginAt',
    header: 'Dernière connexion',
    cell: ({ row }) => (row.original.lastLoginAt ? formatDateTime(row.original.lastLoginAt) : 'Jamais'),
  },
];

function isKnownRole(value: string | undefined): value is BackofficeRole {
  return BACKOFFICE_ROLES.includes(value as BackofficeRole);
}

export function RolePermissionDetailPage(): JSX.Element {
  const { roleId } = useParams();
  const normalizedRole = roleId?.toUpperCase();
  const role = isKnownRole(normalizedRole) ? normalizedRole : undefined;
  const activeRole = role ?? 'AGENT';
  const [selectedCapabilities, setSelectedCapabilities] = useState<Capability[]>(
    () => ROLE_CAPABILITIES[activeRole],
  );

  useEffect(() => {
    setSelectedCapabilities(ROLE_CAPABILITIES[activeRole]);
  }, [activeRole]);

  const roleUsers = useMemo(
    () => USERS.filter((user) => user.role === activeRole),
    [activeRole],
  );

  const enabledGroupCount = useMemo(
    () =>
      CAPABILITY_GROUPS.filter((group) =>
        group.capabilities.some((capability) => selectedCapabilities.includes(capability)),
      ).length,
    [selectedCapabilities],
  );

  // Séparation des tâches (C-34) : un rôle ne devrait pas cumuler la validation
  // financière (approbation de remboursement) ET l'administration technique.
  const cumulatesDuties =
    selectedCapabilities.includes('refund:approve') &&
    (selectedCapabilities.includes('roles:manage') ||
      selectedCapabilities.includes('user:invite') ||
      selectedCapabilities.includes('user:disable'));

  const toggleCapability = (capability: Capability, checked: boolean) => {
    setSelectedCapabilities((current) =>
      checked
        ? Array.from(new Set([...current, capability]))
        : current.filter((item) => item !== capability),
    );
  };

  if (!role) {
    return (
      <EmptyState
        title="Rôle introuvable"
        description="Sélectionnez un rôle depuis la page des rôles et permissions."
        action={
          <Button asChild variant="outline">
            <Link to="/roles-permissions">Retour aux rôles</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Rôles et permissions"
        title={`Modifier ${ROLE_LABELS[activeRole].toLowerCase()}`}
        description={ROLE_DESCRIPTIONS[activeRole]}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button asChild variant="outline">
              <Link to="/roles-permissions">
                <ArrowLeft className="size-4" />
                Retour
              </Link>
            </Button>
            {/* La sauvegarde d'un rôle est une action sensible : elle change
              * les droits de tous les agents du rôle, d'où la confirmation. */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>
                  <Save className="size-4" />
                  Enregistrer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Appliquer les permissions du rôle {ROLE_LABELS[activeRole].toLowerCase()} ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {selectedCapabilities.length} permission(s) s'appliqueront immédiatement aux{' '}
                    {roleUsers.length} agent(s) de ce rôle. La modification des permissions est
                    tracée dans le journal d'audit.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {/* Matérialisation de la séparation des tâches (C-34). */}
                {cumulatesDuties ? (
                  <div className="border-danger/25 bg-danger/5 rounded-lg border p-3 text-sm">
                    <p className="font-medium text-[#991b1b]">Séparation des tâches</p>
                    <p className="text-muted-foreground mt-1">
                      Ce rôle cumulerait la validation financière (approbation de remboursement) et
                      l'administration technique. Vérifiez la séparation des pouvoirs avant
                      d'enregistrer.
                    </p>
                  </div>
                ) : null}
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      toast.success('Rôle enregistré', {
                        description: `${selectedCapabilities.length} permission(s) actives pour ${roleUsers.length} agent(s) — action journalisée.`,
                      })
                    }
                  >
                    Confirmer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />

      <section className="grid gap-4 md:grid-cols-3">
        <MetricCard
          label="Permissions"
          value={`${selectedCapabilities.length}/${ALL_CAPABILITIES.length}`}
          description="Capacités actives dans ce rôle"
          tone={roleTone(activeRole)}
        />
        <MetricCard
          label="Agents concernés"
          value={roleUsers.length}
          description="Comptes utilisant ce rôle"
        />
        <MetricCard
          label="Familles couvertes"
          value={`${enabledGroupCount}/${CAPABILITY_GROUPS.length}`}
          description="Domaines d'accès activés"
        />
      </section>

      <Tabs defaultValue="permissions" className="gap-4">
        <TabsList>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="agents">Agents concernés</TabsTrigger>
        </TabsList>

        <TabsContent value="permissions">
          <div className="grid gap-4 lg:grid-cols-2">
            {CAPABILITY_GROUPS.map((group) => (
              <Card key={group.id} className="gap-4">
                <CardHeader>
                  <SectionHeader title={group.label} description={group.description} />
                </CardHeader>
                <CardContent className="grid gap-2">
                  {group.capabilities.map((capability) => {
                    const id = `${activeRole}-${capability}`;
                    const checked = selectedCapabilities.includes(capability);
                    return (
                      <label
                        key={capability}
                        htmlFor={id}
                        className="flex items-start gap-3 rounded-lg border border-border p-3"
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          onCheckedChange={(value) => toggleCapability(capability, value === true)}
                        />
                        <span className="text-quipux-navy-950 text-sm font-medium leading-5">
                          {CAPABILITY_LABELS[capability]}
                        </span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="agents">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Agents avec ce rôle</CardTitle>
              <CardDescription>
                Vérifier les comptes concernés avant d'ajuster les permissions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={userColumns}
                data={roleUsers}
                mobileCardLabel={`Agents ${ROLE_LABELS[activeRole]}`}
                mobileCard={(user) => (
                  <DataCard
                    eyebrow={ROLE_LABELS[user.role]}
                    title={user.fullName}
                    description={user.email}
                    aside={
                      <Badge variant={user.status === 'ACTIVE' ? 'default' : 'outline'}>
                        {USER_STATUS_LABELS[user.status]}
                      </Badge>
                    }
                    meta={
                      <>
                        <span className="text-muted-foreground">{user.activeSessions} session(s)</span>
                        <span className="text-muted-foreground">
                          {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Jamais connecté'}
                        </span>
                      </>
                    }
                  />
                )}
                emptyState={
                  <EmptyState
                    title="Aucun agent pour ce rôle"
                    description="Les agents associés apparaîtront ici."
                  />
                }
                pagination={{ label: `${roleUsers.length} agent(s)`, hasNextPage: false }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
